<template>
  <div class="d-flex flex-column flex-sm-row">
    <div class="d-flex">
      <v-autocomplete
        :items="predefinedRanges"
        v-model="selectedPredefined"
        @change="onSelectPredefinedRange"
        label="Predefined ranges"
        outlined
        dense
        class="mr-4"
      />

      <v-btn icon @click="toggleMode">
        <v-icon>
          {{ internalMode == "range" ? "mdi-calendar" : "mdi-calendar-range" }}
        </v-icon>
      </v-btn>
    </div>
    <!-- Range Selection -->
    <div
      v-if="internalMode === 'range'"
      class="d-flex"
      :style="{ flexDirection: layoutDirection }"
    >
      <date-picker
        outlined
        dense
        label="Start Date"
        :defaultNowOnNull="false"
        :show-hint="false"
        :show-icon="false"
        v-model="start"
        class="mx-md-2"
      />
      <date-picker
        outlined
        dense
        label="End Date"
        :defaultNowOnNull="false"
        :show-hint="false"
        :show-icon="false"
        v-model="end"
        class="mx-md-2"
      />
    </div>
    <!-- Single Date Selection -->
    <div v-else class="d-flex">
      <v-btn icon @click="prevDate">
        <v-icon>mdi-chevron-left</v-icon>
      </v-btn>
      <date-picker
        :key="date"
        outlined
        dense
        label="Date"
        :defaultNowOnNull="false"
        :show-hint="false"
        :show-icon="false"
        v-model="date"
        class="mx-2"
      />
      <v-btn icon @click="nextDate">
        <v-icon>mdi-chevron-right</v-icon>
      </v-btn>
    </div>
  </div>
</template>

<script>
import moment from "moment";

export default {
  props: {
    mode: {
      type: String,
      default: "range",
      validator: (value) => ["range", "single"].includes(value),
    },
    layout: {
      type: String,
      default: "horizontal",
      validator: (value) => ["horizontal", "vertical"].includes(value),
    },
    value: {
      type: Object,
      default: () => ({}),
    },
  },
  data: () => ({
    windowSize: 0,
    internalMode: "range",
    selectedPredefined: null,
    predefinedRanges: [
      "Today",
      "Yesterday",
      "Tomorrow",
      "Previous week",
      "Current week",
      "Next Week",
      "Previous month",
      "Current month",
      "Next month",
      "Previous quarter",
      "Current quarter",
      "Next quarter",
      "Year to Date",
      "Last year",
    ],
  }),
  watch: {
    mode(newMode) {
      this.internalMode = newMode;
      this.selectedPredefined = null;
    },
  },
  computed: {
    start: {
      get() {
        return this.value.start;
      },
      set(newValue) {
        this.$emit("input", { ...this.value, start: newValue });
      },
    },
    end: {
      get() {
        return this.value.end;
      },
      set(newValue) {
        this.$emit("input", { ...this.value, end: newValue });
      },
    },
    date: {
      get() {
        return this.value.start;
      },
      set(newValue) {
        const dateString = moment(newValue).format("YYYY-MM-DD");
        this.$emit("input", {
          start: dateString,
          end: dateString,
        });
      },
    },
    layoutDirection() {
      if (this.layout === "horizontal" && this.windowSize > 600) {
        return "row";
      }
      return "column";
    },
  },
  methods: {
    updateWindowSize() {
      this.windowSize = window.innerWidth;
    },
    toggleMode() {
      this.internalMode = this.internalMode === "range" ? "single" : "range";
      this.$emit("input", { ...this.value, end: this.value.start });
    },
    prevDate() {
      const prevDate = moment(this.date).subtract(1, "days");
      const formattedDate = prevDate.format("YYYY-MM-DD");
      this.$emit("input", {
        start: formattedDate,
        end: formattedDate,
      });
      this.$emit("on-prev", {
        start: formattedDate,
        end: formattedDate,
      });
    },
    nextDate() {
      const nextDate = moment(this.date).add(1, "days");
      const formattedDate = nextDate.format("YYYY-MM-DD");
      this.$emit("input", {
        start: formattedDate,
        end: formattedDate,
      });
      this.$emit("on-next", {
        start: formattedDate,
        end: formattedDate,
      });
    },
    onSelectPredefinedRange(range) {
      let start, end;

      switch (range) {
        case "Today":
          start = end = moment().format("YYYY-MM-DD");
          break;
        case "Yesterday":
          start = end = moment().subtract(1, "days").format("YYYY-MM-DD");
          break;
        case "Tomorrow":
          start = end = moment().add(1, "days").format("YYYY-MM-DD");
          break;
        case "Previous week":
          start = moment()
            .subtract(1, "week")
            .startOf("week")
            .format("YYYY-MM-DD");
          end = moment().subtract(1, "week").endOf("week").format("YYYY-MM-DD");
          break;
        case "Current week":
          start = moment().startOf("week").format("YYYY-MM-DD");
          end = moment().endOf("week").format("YYYY-MM-DD");
          break;
        case "Next Week":
          start = moment().add(1, "week").startOf("week").format("YYYY-MM-DD");
          end = moment().add(1, "week").endOf("week").format("YYYY-MM-DD");
          break;
        case "Previous month":
          start = moment()
            .subtract(1, "month")
            .startOf("month")
            .format("YYYY-MM-DD");
          end = moment()
            .subtract(1, "month")
            .endOf("month")
            .format("YYYY-MM-DD");
          break;
        case "Current month":
          start = moment().startOf("month").format("YYYY-MM-DD");
          end = moment().endOf("month").format("YYYY-MM-DD");
          break;
        case "Next month":
          start = moment()
            .add(1, "month")
            .startOf("month")
            .format("YYYY-MM-DD");
          end = moment().add(1, "month").endOf("month").format("YYYY-MM-DD");
          break;
        case "Current quarter":
          start = moment().startOf("quarter").format("YYYY-MM-DD");
          end = moment().endOf("quarter").format("YYYY-MM-DD");
          break;
        case "Previous quarter":
          start = moment()
            .subtract(1, "quarter")
            .startOf("quarter")
            .format("YYYY-MM-DD");
          end = moment()
            .subtract(1, "quarter")
            .endOf("quarter")
            .format("YYYY-MM-DD");
          break;
        case "Next quarter":
          start = moment()
            .add(1, "quarter")
            .startOf("quarter")
            .format("YYYY-MM-DD");
          end = moment()
            .add(1, "quarter")
            .endOf("quarter")
            .format("YYYY-MM-DD");
          break;

        case "Year to Date":
          start = moment().startOf("year").format("YYYY-MM-DD");
          end = moment().format("YYYY-MM-DD"); // Until today
          break;
        case "Last year":
          start = moment()
            .subtract(1, "year")
            .startOf("year")
            .format("YYYY-MM-DD");
          end = moment().subtract(1, "year").endOf("year").format("YYYY-MM-DD");
          break;
        default:
          return;
      }

      if (start == end) {
        this.internalMode = "single";
      } else {
        this.internalMode = "range";
      }

      // Update the local state of the date picker.
      if (this.internalMode === "range") {
        this.start = start;
        this.end = end;
      } else {
        this.date = start;
      }

      this.$emit("input", {
        start,
        end,
      });

      this.$emit("on-predefine", {
        start,
        end,
      });
    },
  },
  mounted() {
    this.internalMode = this.mode;
    this.updateWindowSize();
    window.addEventListener("resize", this.updateWindowSize);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateWindowSize);
  },
};
</script>

<style scoped>
</style>
